import { Link } from 'react-router-dom'
import { Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

const EFFECTIVE_DATE_JA = '2026年6月1日'
const EFFECTIVE_DATE_EN = 'June 1, 2026'

function PublicHeader() {
  const { t, i18n } = useTranslation()
  const toggleLang = () => {
    const next = i18n.language === 'ja' ? 'en' : 'ja'
    void i18n.changeLanguage(next)
    localStorage.setItem('i18n-lang', next)
  }
  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
      <div className="mx-auto max-w-5xl flex h-14 items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={toggleLang} className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2">
            {t('legal.switchLang')}
          </button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">{t('legal.login')}</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/signup">{t('legal.startFree')}</Link>
          </Button>
        </div>
      </div>
    </header>
  )
}

function PublicFooter() {
  const { t } = useTranslation()
  return (
    <footer className="border-t py-8 px-6 mt-16">
      <div className="mx-auto max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </div>
        <nav className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap justify-center">
          <Link to="/" className="hover:text-foreground transition-colors">{t('legal.top')}</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">{t('legal.terms')}</Link>
          <Link to="/privacy" className="hover:text-foreground transition-colors">{t('legal.privacy')}</Link>
          <Link to="/legal/commerce" className="hover:text-foreground transition-colors">{t('legal.commerce')}</Link>
        </nav>
        <p className="text-xs text-muted-foreground">© 2026 ScenarioMock</p>
      </div>
    </footer>
  )
}

function TermsJa() {
  return (
    <>
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">利用規約</h1>
        <p className="text-sm text-muted-foreground">施行日: {EFFECTIVE_DATE_JA}</p>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        本利用規約（以下「本規約」）は、ScenarioMock（以下「本サービス」）の利用条件を定めるものです。
        本サービスを利用される前に、必ず本規約をお読みください。
        利用登録の完了または本サービスの利用開始をもって、本規約に同意したものとみなします。
      </p>
      <Separator />
      <Section title="第1条（定義）">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>「本サービス」とは、ScenarioMock が提供する Mock API サーバーおよび関連する Web アプリケーションを指します。</li>
          <li>「ユーザー」とは、本規約に同意のうえ本サービスを利用する個人または法人を指します。</li>
          <li>「コンテンツ」とは、ユーザーが本サービス上に登録・作成したエンドポイント、シナリオ、レスポンス定義等を指します。</li>
        </ol>
      </Section>
      <Section title="第2条（利用登録）">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>本サービスは、有効なメールアドレスを用いてアカウント登録した方が利用できます。</li>
          <li>未成年者が利用する場合は、保護者の同意を得てください。</li>
          <li>登録情報に虚偽がある場合、または本規約に違反する場合、アカウントを停止・削除することがあります。</li>
          <li>アカウントの管理はユーザーの責任において行うものとし、第三者による不正利用について当社は責任を負いません。</li>
        </ol>
      </Section>
      <Section title="第3条（料金・支払い）">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>本サービスには無料プランと有料プラン（Pro）があります。各プランの機能・制限は本サービス上に掲載するものとします。</li>
          <li>有料プランの料金は月額制とし、Stripe を通じてクレジットカードで決済します。</li>
          <li>一度支払われた料金は、法令に定める場合を除き返金しません。</li>
          <li>当社は料金を変更する場合、30日前までにユーザーに通知します。</li>
        </ol>
      </Section>
      <Section title="第4条（禁止事項）">
        <p className="text-sm leading-relaxed text-muted-foreground mb-3">ユーザーは以下の行為をしてはなりません。</p>
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>法令または公序良俗に違反する行為</li>
          <li>本サービスのサーバーやネットワークに過大な負荷をかける行為</li>
          <li>スパム、フィッシング、マルウェア配布等の不正行為に本サービスを利用すること</li>
          <li>他のユーザーまたは第三者の権利・利益を侵害する行為</li>
          <li>本サービスの運営を妨害する行為</li>
          <li>当社の許可なく本サービスを商業目的で転売・再販する行為</li>
          <li>その他、当社が不適切と判断する行為</li>
        </ol>
      </Section>
      <Section title="第5条（サービスの変更・停止）">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>当社は、ユーザーへの事前通知なしにサービス内容を変更・追加・削除することがあります。</li>
          <li>メンテナンス、障害、天災等の理由によりサービスを一時停止することがあります。</li>
          <li>サービスを終了する場合は、90日前までにユーザーへ通知します。</li>
        </ol>
      </Section>
      <Section title="第6条（免責事項）">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>本サービスは現状有姿で提供するものとし、特定目的への適合性、可用性、正確性について保証しません。</li>
          <li>本サービスの利用によって生じた損害（業務の中断、データの損失等）について、当社は一切の責任を負いません。</li>
          <li>本サービスは Mock・テスト用途を想定しており、本番トラフィックの代替として使用することは推奨しません。</li>
        </ol>
      </Section>
      <Section title="第7条（知的財産権）">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>本サービスに関する著作権その他の知的財産権は当社に帰属します。</li>
          <li>ユーザーが作成したコンテンツの知的財産権はユーザーに帰属します。</li>
          <li>ユーザーは、当社がサービス提供・改善・マーケティングのためにコンテンツを利用することを許諾するものとします。</li>
        </ol>
      </Section>
      <Section title="第8条（退会・データ削除）">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>ユーザーはいつでも退会（アカウント削除）の申請ができます。</li>
          <li>退会完了後、ユーザーのアカウント情報およびコンテンツは30日以内に削除されます。</li>
          <li>リクエストログはセキュリティ上の理由から最大30日間保持した後に削除されます。</li>
        </ol>
      </Section>
      <Section title="第9条（本規約の変更）">
        <p className="text-sm leading-relaxed text-muted-foreground">
          当社は、必要に応じて本規約を変更することがあります。変更内容は本サービス上に掲載し、
          重要な変更の場合は登録メールアドレスに通知します。変更後も本サービスを利用し続けた場合、
          改定後の規約に同意したものとみなします。
        </p>
      </Section>
      <Section title="第10条（準拠法・管轄裁判所）">
        <p className="text-sm leading-relaxed text-muted-foreground">
          本規約は日本法に準拠します。本サービスに関して紛争が生じた場合、
          東京地方裁判所を第一審の専属的合意管轄裁判所とします。
        </p>
      </Section>
      <Separator />
      <p className="text-sm text-muted-foreground">
        本規約に関するお問い合わせ:{' '}
        <a href="mailto:support@scenariomock.com" className="text-primary underline underline-offset-4">
          support@scenariomock.com
        </a>
      </p>
    </>
  )
}

function TermsEn() {
  return (
    <>
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Terms of Service</h1>
        <p className="text-sm text-muted-foreground">Effective date: {EFFECTIVE_DATE_EN}</p>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        These Terms of Service ("Terms") govern your use of ScenarioMock ("Service").
        Please read these Terms carefully before using the Service.
        By completing registration or using the Service, you agree to be bound by these Terms.
      </p>
      <Separator />
      <Section title="Article 1 (Definitions)">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>"Service" means the Mock API server and related web application provided by ScenarioMock.</li>
          <li>"User" means any individual or entity that agrees to these Terms and uses the Service.</li>
          <li>"Content" means endpoints, scenarios, response definitions, and other materials that Users register or create on the Service.</li>
        </ol>
      </Section>
      <Section title="Article 2 (Registration)">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>The Service is available to anyone who registers an account with a valid email address.</li>
          <li>Minors must obtain parental or guardian consent before using the Service.</li>
          <li>We may suspend or delete your account if registration information is false or if you violate these Terms.</li>
          <li>You are responsible for managing your account, and we are not liable for unauthorized use by third parties.</li>
        </ol>
      </Section>
      <Section title="Article 3 (Fees and Payment)">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>The Service offers a Free plan and a paid Pro plan. Features and limitations of each plan are posted on the Service.</li>
          <li>Pro plan fees are billed monthly and processed via credit card through Stripe.</li>
          <li>All fees paid are non-refundable except as required by applicable law.</li>
          <li>We will notify Users at least 30 days in advance of any fee changes.</li>
        </ol>
      </Section>
      <Section title="Article 4 (Prohibited Activities)">
        <p className="text-sm leading-relaxed text-muted-foreground mb-3">Users must not engage in any of the following activities:</p>
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>Violating any applicable laws or public order and morals</li>
          <li>Placing excessive load on the Service's servers or networks</li>
          <li>Using the Service for spam, phishing, malware distribution, or other fraudulent activities</li>
          <li>Infringing the rights or interests of other users or third parties</li>
          <li>Interfering with the operation of the Service</li>
          <li>Reselling or redistributing the Service for commercial purposes without our permission</li>
          <li>Any other activities we deem inappropriate</li>
        </ol>
      </Section>
      <Section title="Article 5 (Service Changes and Suspension)">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>We may modify, add, or remove Service features without prior notice to Users.</li>
          <li>We may temporarily suspend the Service for maintenance, failures, natural disasters, or other reasons.</li>
          <li>We will notify Users at least 90 days before terminating the Service.</li>
        </ol>
      </Section>
      <Section title="Article 6 (Disclaimer)">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>The Service is provided "as is" without warranties of fitness for a particular purpose, availability, or accuracy.</li>
          <li>We are not liable for any damages arising from use of the Service, including business interruption or data loss.</li>
          <li>The Service is intended for mock and testing purposes and is not recommended as a replacement for production traffic.</li>
        </ol>
      </Section>
      <Section title="Article 7 (Intellectual Property)">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>All copyrights and other intellectual property rights related to the Service belong to us.</li>
          <li>Intellectual property rights in Content created by Users belong to the respective Users.</li>
          <li>Users grant us a license to use Content for the purpose of providing, improving, and marketing the Service.</li>
        </ol>
      </Section>
      <Section title="Article 8 (Cancellation and Data Deletion)">
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>Users may request account deletion at any time.</li>
          <li>After cancellation, account information and Content will be deleted within 30 days.</li>
          <li>Request logs are retained for up to 30 days for security purposes before deletion.</li>
        </ol>
      </Section>
      <Section title="Article 9 (Amendment of Terms)">
        <p className="text-sm leading-relaxed text-muted-foreground">
          We may amend these Terms as necessary. Changes will be posted on the Service, and significant changes will be
          notified to your registered email address. Continued use of the Service after changes constitutes acceptance of the revised Terms.
        </p>
      </Section>
      <Section title="Article 10 (Governing Law and Jurisdiction)">
        <p className="text-sm leading-relaxed text-muted-foreground">
          These Terms are governed by the laws of Japan. Any disputes arising in connection with the Service
          shall be subject to the exclusive jurisdiction of the Tokyo District Court as the court of first instance.
        </p>
      </Section>
      <Separator />
      <p className="text-sm text-muted-foreground">
        For inquiries regarding these Terms:{' '}
        <a href="mailto:support@scenariomock.com" className="text-primary underline underline-offset-4">
          support@scenariomock.com
        </a>
      </p>
    </>
  )
}

export function TermsPage() {
  const { i18n } = useTranslation()
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-6 py-16 space-y-10">
          {i18n.language === 'ja' ? <TermsJa /> : <TermsEn />}
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {children}
    </section>
  )
}
